import { stringifyEntityRef } from '@backstage/catalog-model';

/**
 * How the portal records the pages a reader opens (DX-349).
 *
 * The history is Backstage's own — `VisitListener` writes it, the palette's
 * Recent and the home page's visit cards read it — and these are the three
 * decisions the portal makes on top of it. They live here, at module level,
 * rather than inline where they are used, for two reasons: `VisitListener`
 * re-runs its save whenever one of these functions changes identity, so an
 * inline default re-saved the same page on every re-render (a filter changing
 * the query string counted as another visit); and the palette reads the
 * current page's entity the same way the listener records it.
 */

/** `/catalog/<namespace>/<kind>/<name>`, and whatever tab follows it. */
const ENTITY_ROUTE = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;

/**
 * One visit per entity, whichever of its tabs was open.
 *
 * The history is keyed by pathname and keeps the last hundred. Without this,
 * `…/api/score-api` and `…/api/score-api/definition` were two entries: the home
 * page's Recently visited listed the API once per tab, and a reader moving
 * through one entity's tabs pushed other entities out of the hundred.
 */
export const collapseEntityTabs = (pathname: string): string =>
  ENTITY_ROUTE.exec(pathname)?.[0] ?? pathname;

/** The entity a page belongs to, as a ref, or nothing for any other page. */
export const toEntityRef = ({
  pathname,
}: {
  pathname: string;
}): string | undefined => {
  const match = ENTITY_ROUTE.exec(pathname);
  return match
    ? stringifyEntityRef({
        namespace: match[1],
        kind: match[2],
        name: match[3],
      })
    : undefined;
};

/**
 * What a visit is called: an entity's name, a top-level page's segment, or
 * else the document's title — the rule Backstage applies when none is given.
 */
export const visitName = ({ pathname }: { pathname: string }): string =>
  ENTITY_ROUTE.exec(pathname)?.[3] ??
  /^\/([^/]+)$/.exec(pathname)?.[1] ??
  document.title;
